import {
  AiChatActionType,
  AiProcessStatus,
  ChatMessageType,
  ChatSession,
  InvoiceItem,
  MessageActor,
  SendMessageRequest,
  ShipmentStatus
} from '@/features/AiChat/model/api/chat/types.ts';
import { DEFAULT_ADVANCE_VALUE } from '@/features/AiChat/model/constants';
import { ExtendedFile, SessionService } from '@/features/AiChat/model/context/types.ts';
import { getChatSessionApi, postMessageApi } from '@/features/AiChat/model/api/chat';
import { isProposalDecisionPending } from '@/features/AiChat/model/isProposalDecisionPending';
import { isRemainingItemsReviewMessage } from '@/features/AiChat/model/isRemainingItemsReviewMessage';
import { useAiSessionStatus } from '@/features/AiChat/model/services/use-ai-session-status.ts';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSessionServiceParams {
  canUseAi: boolean;
  scopeKey?: string;
}

export const useSessionService = (params: UseSessionServiceParams): SessionService => {
  const {
    canUseAi,
    scopeKey
  } = params;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<ExtendedFile[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [activeSession, setActiveSessionState] = useState<ChatSession | null>(null);
  const activeSessionIdRef = useRef<number | undefined>(undefined);
  const activeSessionGenerationRef = useRef(0);
  const messageOperationRef = useRef(0);
  const accessScopeRef = useRef({ canUseAi, scopeKey });
  accessScopeRef.current = { canUseAi, scopeKey };
  const [isMessageSending, setIsMessageSending] = useState<boolean>(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [advanceValue, setAdvanceValueState] = useState<number>(DEFAULT_ADVANCE_VALUE);
  const [isAdvanceFromInvoice, setIsAdvanceFromInvoice] = useState<boolean>(false);
  const isAdvanceManuallyEditedRef = useRef<boolean>(false);

  useEffect(() => {
    accessScopeRef.current = { canUseAi, scopeKey };
    messageOperationRef.current += 1;
    setInvoiceItems([]);
    setIsFileModalOpen(false);
    setIsMessageSending(false);
    return () => {
      accessScopeRef.current = { canUseAi: false, scopeKey };
      messageOperationRef.current += 1;
    };
  }, [canUseAi, scopeKey]);

  const getInvoiceAdvanceValue = (percent?: number | null): number | null => {
    if (typeof percent !== 'number' || Number.isNaN(percent) || percent < 15) {
      return null;
    }

    const max = 100;
    return Math.min(max, Math.round(percent));
  };

  const setAdvanceValue = useCallback((value: number, isManual = true): void => {
    if (isManual) {
      isAdvanceManuallyEditedRef.current = true;
      setIsAdvanceFromInvoice(false);
    }

    setAdvanceValueState(value);
  }, []);

  const setActiveSession = useCallback((session: ChatSession | null): void => {
    const isNewSession = activeSessionIdRef.current !== session?.id;
    activeSessionIdRef.current = session?.id;

    if (isNewSession) {
      activeSessionGenerationRef.current += 1;
      isAdvanceManuallyEditedRef.current = false;
      const invoiceAdvance = getInvoiceAdvanceValue(session?.invoice_advance_percent);

      if (invoiceAdvance !== null) {
        setAdvanceValueState(invoiceAdvance);
        setIsAdvanceFromInvoice(true);
      } else {
        setAdvanceValueState(DEFAULT_ADVANCE_VALUE);
        setIsAdvanceFromInvoice(false);
      }
    } else if (!isAdvanceManuallyEditedRef.current) {
      const invoiceAdvance = getInvoiceAdvanceValue(session?.invoice_advance_percent);

      if (invoiceAdvance !== null) {
        setAdvanceValueState(invoiceAdvance);
        setIsAdvanceFromInvoice(true);
      }
    }

    setActiveSessionState(session);
  }, []);

  const updateSessionApi = useCallback(() => {
    const sessionId = activeSessionIdRef.current;
    const generation = activeSessionGenerationRef.current;
    const requestScope = accessScopeRef.current.scopeKey;
    if (sessionId === undefined || !accessScopeRef.current.canUseAi) return;

    getChatSessionApi(sessionId)
      .then((res) => {
        if (
          !accessScopeRef.current.canUseAi
          || accessScopeRef.current.scopeKey !== requestScope
          || activeSessionGenerationRef.current !== generation
        ) return;

        setActiveSession(res);
      }).catch(console.error);
  }, [setActiveSession]);

  useEffect(() => {
    if (activeSession?.message_history) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200)
    }
  }, [activeSession?.message_history]);

  const { aiProcessCurrentStep, aiProcessStatus, setAiProcessStatus, startPolling, stopPolling } = useAiSessionStatus({
    canPoll: canUseAi,
    onSuccess: updateSessionApi,
    scopeKey
  });

  const getLastAgentMessageId = (): number | undefined => {
    const clone = activeSession?.message_history ? [...activeSession.message_history] : [];
    return clone.reverse()
      .find((msg) => msg.actor === MessageActor.AGENT)?.message_id;
  };

  const removeFile = (clientId: string): void => {
    const nextFiles = selectedFiles.filter((file) => file.clientId !== clientId);
    if (!nextFiles.length) {
      setIsFileModalOpen(false);
    }
    setSelectedFiles(nextFiles);
  };

  const addMessageToHistory = (message: ChatMessageType, sessionId = activeSession?.id): void => {
    setActiveSessionState((prevSession) => {
      if (prevSession?.id !== sessionId) {
        return prevSession;
      }

      return {
        ...prevSession,
        message_history: [...prevSession.message_history, message]
      };
    });
  };

  const sendMessage = (files: ExtendedFile[], sessionId?: number): Promise<boolean> => {
    const targetSessionId = sessionId ?? activeSession?.id;
    const serverFileIds = files.map((item) => item.serverFileId);
    const hasIncompleteFiles = serverFileIds.some((fileId) => typeof fileId !== 'number');
    const hasFilesFromAnotherSession = files.some((file) => file.sessionId !== targetSessionId);

    if (
      hasIncompleteFiles
      || hasFilesFromAnotherSession
      || !canUseAi
      || isProposalDecisionPending(activeSession)
      || (!serverFileIds.length && !inputValue)
      || targetSessionId === undefined
      || activeSessionIdRef.current !== targetSessionId
    ) {
      return Promise.resolve(false);
    }

    const uploadedFileIds = serverFileIds.filter(
      (fileId): fileId is number => typeof fileId === 'number'
    );

    const lastAgentMessageId = getLastAgentMessageId();

    const message: SendMessageRequest = {
      session_id: targetSessionId,
      user_text: inputValue,
      file_ids: uploadedFileIds,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      action_id: '',
      reply_message_id: lastAgentMessageId
    };

    return postMessage(message, uploadedFileIds);
  };

  const sendMessageAction = (actionType: AiChatActionType) => {
    if (!canUseAi || !activeSession) return;

    const isProposalDecisionAction = actionType === AiChatActionType.ACCEPT_PROPOSAL
      || actionType === AiChatActionType.REJECT_PROPOSAL;

    if (isProposalDecisionAction && activeSession.requires_revised_invoice) return;
    if (isProposalDecisionPending(activeSession) && !isProposalDecisionAction) return;

    const lastAgentMessageId = getLastAgentMessageId();
    let userText;
    let timeout = 2000;

    if (actionType === AiChatActionType.CONFIRM_REMAINING_ITEMS && (
      !isRemainingItemsReviewMessage(activeSession, lastAgentMessageId)
      || aiProcessStatus !== AiProcessStatus.IDLE
      || isMessageSending
    )) return;

    if (actionType === AiChatActionType.CONFIRM_ITEMS && (
      activeSession.remaining_composition_review
      || activeSession.shipment_status !== ShipmentStatus.DRAFT
    )) return;

    switch (actionType) {
      case AiChatActionType.CONFIRM_ITEMS:
        userText = '✅ Состав подтверждаю';
        timeout = 10000;
        break;
      case AiChatActionType.CONFIRM_REMAINING_ITEMS:
        userText = 'Подтверждаю оставшийся состав и сумму';
        timeout = 10000;
        break;
      case AiChatActionType.ACCEPT_PROPOSAL:
        userText = '✅ Акцептую бюджет, запускаем!';
        break;
      case AiChatActionType.REJECT_PROPOSAL:
        userText = '❌ Не акцептую бюджет';
        break;
      case AiChatActionType.YES:
        userText = 'Да';
        break;
      case AiChatActionType.NO:
        userText = 'Нет';
        break;
      case AiChatActionType.INCLUDE_PAYMENT_AGENT:
        userText = 'Да, включить в КП';
        break;
      case AiChatActionType.DECLINE_PAYMENT_AGENT:
        userText = 'Нет, не требуется';
        break;
      case AiChatActionType.ACCEPT:
        userText = String(advanceValue);
        break;
      default:
        userText = 'Неизвестный action';
    }

    const message: SendMessageRequest = {
      session_id: activeSession.id,
      user_text: userText,
      file_ids: [],
      action_id: actionType,
      reply_message_id: lastAgentMessageId
    };

    void postMessage(message, [], timeout);
  };

  const sendMessageChips = (value: string) => {
    if (!canUseAi || !activeSession || isProposalDecisionPending(activeSession)) return;

    const lastAgentMessageId = getLastAgentMessageId();

    const message: SendMessageRequest = {
      session_id: activeSession.id,
      user_text: value,
      file_ids: [],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      action_id: '',
      reply_message_id: lastAgentMessageId
    };

    void postMessage(message, [], 4000);
  };

  const postMessage = async (
    message: SendMessageRequest,
    successFiles: number[],
    delay?: number
  ): Promise<boolean> => {
    const generation = activeSessionGenerationRef.current;
    const messageOperation = ++messageOperationRef.current;
    const requestScope = accessScopeRef.current.scopeKey;
    setIsMessageSending(true);

    try {
      const response = await postMessageApi(message);
      if (!accessScopeRef.current.canUseAi || accessScopeRef.current.scopeKey !== requestScope) return false;
      if (
        activeSessionIdRef.current !== message.session_id
        || activeSessionGenerationRef.current !== generation
      ) {
        return true;
      }

      addMessageToHistory(response, message.session_id);
      const timeout = delay
        ? delay
        : successFiles.length
          ? Math.min(5000 + successFiles.length * 1000, 10000)
          : 2000;
      startPolling(message.session_id, timeout);

      setIsFileModalOpen(false);
      setSelectedFiles([]);
      setInputValue('');
      return true;
    } catch {
      return false;
    } finally {
      if (messageOperation === messageOperationRef.current) {
        setTimeout(() => {
          if (messageOperation === messageOperationRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
        setIsMessageSending(false);
      }
    }
  };

  return {
    methods: {
      addMessageToHistory,
      removeFile,
      sendMessage,
      sendMessageAction,
      sendMessageChips,
      setActiveSession,
      setAdvanceValue,
      setAiProcessStatus,
      setInputValue,
      setInvoiceItems,
      setIsFileModalOpen,
      setSelectedFiles,
      startPolling,
      stopPolling
    },
    state: {
      activeSession,
      advanceValue,
      aiProcessCurrentStep,
      aiProcessStatus,
      inputValue,
      invoiceItems,
      isAdvanceFromInvoice,
      isFileModalOpen,
      isMessageSending,
      messagesEndRef,
      selectedFiles
    }
  };
};